import { Inertia } from '@inertiajs/inertia'
import { isEmbedded } from './tracking'

// Obnova session průvodce v iframu po celém načtení dokumentu.
//
// Když prohlížeč blokuje cookies třetích stran, drží session jen token
// v hlavičce X-Iframe-Token u požadavků Inertie. Celé načtení rámce (ruční
// obnovení, odpověď 409 po nasazení nové verze) hlavičku neposílá a server
// založí prázdnou session. Token proto iframe drží v sessionStorage svého
// původu, kam skripty vkládajícího webu nedosáhnou, a po načtení si
// poslední stránku vyžádá znovu s hlavičkou. Do URL se nic tajného nedává.

const TOKEN_HEADER = 'X-Iframe-Token'

// Obnovit jde jen veřejné GET stránky zákazníka na našem původu. Hlavička
// s tokenem se nesmí dostat na cizí server ani na jinou cestu.
const RESUMABLE_PATH = /^\/(rezervace|detail-rezervace|vyzvednuti)\//

// Dva iframy průvodce v jedné kartě sdílí sessionStorage, klíč proto nese
// identifikátor rámce. window.name přežije obnovení rámce a není tajný.
// Jméno, které rámci dal partner, zůstává: mohou na něj mířit jeho odkazy.
function frameKey() {
  if (!window.name) {
    const random = new Uint32Array(2)
    window.crypto.getRandomValues(random)
    window.name = `parkovato-${random[0].toString(36)}${random[1].toString(36)}`
  }
  return `parkovato:iframe-session:${window.name}`
}

function read() {
  try {
    return JSON.parse(window.sessionStorage.getItem(frameKey()) || 'null')
  } catch (e) {
    return null
  }
}

function write(patch) {
  try {
    window.sessionStorage.setItem(frameKey(), JSON.stringify({ ...(read() || {}), ...patch }))
  } catch (e) {
    // Bez úložiště se chováme jako dřív: po celém načtení session nepřežije.
  }
}

function clear() {
  try {
    window.sessionStorage.removeItem(frameKey())
  } catch (e) {}
}

function navigationType() {
  try {
    return window.performance.getEntriesByType('navigation')[0]?.type ?? null
  } catch (e) {
    return null
  }
}

const withoutHash = (value) => {
  try {
    const url = new URL(value, window.location.href)
    url.hash = ''
    return url.href
  } catch (e) {
    return null
  }
}

// Cíl odpovědi 409 platí jen pro načtení, které z něj skutečně vzniklo:
// krátce po uložení a z téže stránky. Zrušená navigace (beforeunload,
// odpověď 204) jinak nechá cíl uložený a přebil by pozdější navigaci.
const LOCATION_TTL_MS = 120 * 1000

function freshLocationUrl(saved, restoring) {
  if (!saved.locationUrl || !(Date.now() - saved.locationAt < LOCATION_TTL_MS)) return null
  if (!restoring && withoutHash(document.referrer) !== withoutHash(saved.locationFrom)) return null
  return resumableUrl(saved.locationUrl)
}

function resumableUrl(value) {
  if (!value) return null
  try {
    const url = new URL(value, window.location.href)
    return url.origin === window.location.origin && RESUMABLE_PATH.test(url.pathname) ? url : null
  } catch (e) {
    return null
  }
}

/**
 * Stránka a token, se kterými se má aplikace spustit.
 *
 * Mimo iframe nebo s tokenem v dokumentu se nic nemění. Jinak zkusí podle
 * uloženého tokenu načíst poslední stránku průvodce. Nepovede-li se to,
 * spustí aplikaci s dokumentem, jak přišel ze serveru.
 */
export async function resolveIframeSession(documentPage, documentToken) {
  if (!isEmbedded || documentToken) {
    if (isEmbedded) write({ token: documentToken, committedUrl: null, locationUrl: null, retried: false })
    return { page: documentPage, token: documentToken }
  }

  // Na poslední stránku se vracíme jen po obnovení rámce nebo návratu
  // v historii. Když partner rámec pošle na jinou adresu, platí jeho volba.
  // Cíl odpovědi 409 platí, jen když z něj toto načtení vzniklo.
  const saved = read()
  const restoring = ['reload', 'back_forward'].includes(navigationType())
  const target = saved?.token ? freshLocationUrl(saved, restoring) || (restoring ? resumableUrl(saved.committedUrl) : null) : null
  // Cíl 409 se spotřebuje hned, jiné načtení už ho použít nesmí.
  if (saved?.locationUrl) write({ locationUrl: null, locationAt: null, locationFrom: null })
  if (!target) return { page: documentPage, token: null }

  try {
    const response = await window.axios.get(target.href, {
      headers: {
        'X-Inertia': 'true',
        'X-Inertia-Version': documentPage.version,
        [TOKEN_HEADER]: saved.token,
        Accept: 'text/html, application/xhtml+xml',
      },
    })
    const page = response.data

    // Obnova platí, jen když odpověď patří session s naším tokenem. Vypršelý
    // token vrátí prázdnou session s jiným nebo žádným tokenem.
    if (response.headers['x-inertia'] && page?.props?.iframeToken === saved.token) {
      write({ retried: false })
      return { page, token: saved.token }
    }

    clear()
  } catch (error) {
    // Mezi načtením dokumentu a tímto požadavkem proběhlo další nasazení.
    // Jedno nové načtení stačí, token i cíl zůstávají uložené.
    if (error.response?.status === 409 && !saved.retried) {
      write({ locationUrl: target.href, locationAt: Date.now(), locationFrom: saved.locationFrom, retried: true })
      window.location.reload()
      return new Promise(() => {})
    }
    // Síťová chyba nebo chyba serveru: token nechat pro další pokus.
  }

  return { page: documentPage, token: null }
}

/**
 * Průběžně ukládá, kam se má iframe po celém načtení vrátit.
 *
 * Potvrzená stránka se bere z úspěšných navigací. Cíl odpovědi 409 vidí
 * spolehlivě jen Inertia.locationVisit(): událost `before` u odeslání
 * formuláře zná jen adresu POST, ne cílovou stránku po přesměrování.
 * Obaluje interní metodu Inertie, verze balíčku je proto v package.json
 * pevně daná.
 */
export function trackIframeSession(token) {
  if (!isEmbedded || !token) return

  const commit = (event) => write({ token, committedUrl: event.detail.page.url, locationUrl: null, locationAt: null, locationFrom: null })
  Inertia.on('navigate', commit)
  Inertia.on('success', commit)

  const locationVisit = Inertia.locationVisit.bind(Inertia)
  Inertia.locationVisit = (url, preserveScroll) => {
    write({ locationUrl: url.href, locationAt: Date.now(), locationFrom: window.location.href })
    const result = locationVisit(url, preserveScroll)
    if (result === false) write({ locationUrl: null, locationAt: null, locationFrom: null })
    return result
  }
}

export { TOKEN_HEADER }
